import {
  BarChart3,
  CalendarDays,
  Building2,
  Gift,
  Handshake,
  HeartHandshake,
  Kanban,
  LayoutDashboard,
  Library,
  Link2,
  LogOut,
  Mail,
  Settings,
  Target,
  UsersRound,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { AppShell, type NavGroup } from "@otii/ui/shell";
import { post } from "../lib/api";
import { useMe } from "../lib/queries";
import { GmailDialog } from "../features/mail/GmailDialog";
import { CommandPalette } from "./CommandPalette";

/**
 * The working menu in three groups, divided by lines: strategy and oversight,
 * the databases, then supplementary actions. Admin sits apart at the bottom.
 */
const NAV: NavGroup[] = [
  {
    label: "Strategy and oversight",
    items: [
      { to: "/", label: "Home", icon: LayoutDashboard, end: true },
      { to: "/calendar", label: "Calendar", icon: CalendarDays },
      { to: "/objectives", label: "Objectives", icon: Target },
      { to: "/pipeline", label: "Pipeline", icon: Kanban },
      { to: "/insights", label: "Insights", icon: BarChart3 },
    ],
  },
  {
    label: "Databases",
    items: [
      { to: "/customers", label: "Customers", icon: HeartHandshake },
      { to: "/prospects", label: "Prospects", icon: Building2 },
      { to: "/partners", label: "Partners", icon: Handshake },
      { to: "/contacts", label: "Contacts", icon: UsersRound },
    ],
  },
  {
    label: "Actions",
    items: [
      { to: "/library", label: "Library", icon: Library },
      { to: "/email", label: "Email", icon: Mail },
      { to: "/links", label: "Links", icon: Link2 },
      { to: "/referrals", label: "Referrals", icon: Gift },
    ],
  },
];

export function Shell() {
  const { data: me } = useMe();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = me?.user.role === "admin";
  const [gmailOpen, setGmailOpen] = useState(false);
  const [search, setSearch] = useSearchParams();

  // Back from Google's consent page for Gmail.
  useEffect(() => {
    const connected = search.get("gmail");
    const failed = search.get("gmail_error");
    if (!connected && !failed) return;
    if (failed) toast.error(failed);
    else toast.success("Gmail connected");
    setGmailOpen(true);
    const next = new URLSearchParams(search);
    next.delete("gmail");
    next.delete("gmail_error");
    setSearch(next, { replace: true });
  }, [search, setSearch]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const signOut = async () => {
    await post("/api/auth/logout");
    qc.clear();
    navigate("/sign-in");
  };

  return (
    <>
      <AppShell
        product="Hub"
        tagline="Sales & customers"
        groups={NAV}
        pinned={isAdmin ? { to: "/admin", label: "Admin", icon: Settings } : null}
        user={me ? { name: me.user.name, avatarUrl: me.user.avatarUrl, roleLabel: me.user.role === "admin" ? "Admin" : "Team" } : null}
        userMenu={[{ label: "Gmail", icon: <Mail />, onSelect: () => setGmailOpen(true) }, "separator", { label: "Sign out", icon: <LogOut />, onSelect: signOut }]}
        onSearch={() => setPaletteOpen(true)}
      >
        <Outlet />
      </AppShell>
      <GmailDialog open={gmailOpen} onOpenChange={setGmailOpen} />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
